import React, { useState } from 'react';
import useClients from '../hooks/useClients';
import useEntryFilters from '../hooks/useEntryFilters';
import useRequestedBy from '../hooks/useRequestedBy';
import ReportCharts from '../components/ReportCharts';

// Default date range: 1st of current month → today
function defaultStartDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function defaultEndDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PRESETS = [
  { label: 'This week',  getRange: () => { const d = new Date(); const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay()+6)%7)); return [fmt(mon), fmt(d)]; } },
  { label: 'This month', getRange: () => [defaultStartDate(), defaultEndDate()] },
  { label: 'Last month', getRange: () => { const d = new Date(); d.setDate(1); d.setDate(0); const start = new Date(d.getFullYear(), d.getMonth(), 1); return [fmt(start), fmt(d)]; } },
  { label: 'This year',  getRange: () => { const d = new Date(); return [`${d.getFullYear()}-01-01`, fmt(d)]; } },
];
function fmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function Reports() {
  const [pendingFilters, setPendingFilters] = useState({
    startDate: defaultStartDate(),
    endDate: defaultEndDate(),
  });
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: defaultStartDate(),
    endDate: defaultEndDate(),
  });

  const clients = useClients();
  const { users, categories, departments } = useEntryFilters();
  const requestedByList = useRequestedBy();

  const selectClass = 'border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 w-full';

  return (
    <div className="p-4 dark:bg-gray-900 dark:text-gray-100 min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Reports</h2>
        <button
          onClick={() => setAppliedFilters({ ...pendingFilters })}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded font-semibold text-sm shadow"
        >
          Generate Report
        </button>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map(p => (
          <button
            key={p.label}
            onClick={() => {
              const [s, e] = p.getRange();
              const next = { ...pendingFilters, startDate: s, endDate: e };
              setPendingFilters(next);
              setAppliedFilters(next);
            }}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6 bg-gray-50 dark:bg-gray-800 p-4 rounded shadow">
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Start Date</label>
          <input type="date" value={pendingFilters.startDate || ''} className={selectClass}
            onChange={e => setPendingFilters(f => ({ ...f, startDate: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">End Date</label>
          <input type="date" value={pendingFilters.endDate || ''} className={selectClass}
            onChange={e => setPendingFilters(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">User</label>
          <select className={selectClass} value={pendingFilters.userId || ''}
            onChange={e => setPendingFilters(f => ({ ...f, userId: e.target.value }))}>
            <option value="">All Users</option>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Client</label>
          <select className={selectClass} value={pendingFilters.clientId || ''}
            onChange={e => setPendingFilters(f => ({ ...f, clientId: e.target.value }))}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Category</label>
          <select className={selectClass} value={pendingFilters.category || ''}
            onChange={e => setPendingFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Department</label>
          <select className={selectClass} value={pendingFilters.department || ''}
            onChange={e => setPendingFilters(f => ({ ...f, department: e.target.value }))}>
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Requested By</label>
          <select className={selectClass} value={pendingFilters.requestedBy || ''}
            onChange={e => setPendingFilters(f => ({ ...f, requestedBy: e.target.value }))}>
            <option value="">All</option>
            {requestedByList.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <ReportCharts filters={appliedFilters} />
    </div>
  );
}
